
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { Profile } from '../types/user';

interface AuthContextType {
  currentUser: Profile | null;
  viewingAsUser: Profile | null;
  isAuthenticated: boolean;
  isAdmin: () => boolean;
  isKunde: () => boolean;
  isCaller: () => boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  startImpersonation: (userId: string) => void;
  stopImpersonation: () => void;
  isImpersonating: () => boolean;
  getEffectiveUser: () => Profile | null;
  updateCurrentUser: (updates: Partial<Profile>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const IMPERSONATION_STORAGE_KEY = 'fb_lead_impersonation';

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<Profile | null>(null);
  const [viewingAsUser, setViewingAsUser] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  // Load profile from Supabase
  const loadUserProfile = async (userId: string): Promise<Profile | null> => {
    try {
      console.log('🔍 Loading profile for user ID:', userId);

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      console.log('📊 Profile query result:', { data, error });

      if (error) {
        console.error('❌ Error loading user profile:', error);
        console.error('Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        return null;
      }

      if (!data) {
        console.error('❌ No profile data returned for user:', userId);
        return null;
      }

      console.log('✅ Profile loaded successfully:', data);
      return data as Profile;
    } catch (error) {
      console.error('❌ Exception loading user profile:', error);
      return null;
    }
  };

  // Initialize auth state
  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      try {
        console.log('🚀 Initializing auth...');

        // Simple session check without timeout - let it complete naturally
        const { data: { session }, error } = await supabase.auth.getSession();

        if (!mounted) return; // Component unmounted, don't update state

        if (error) {
          console.error('❌ Error getting session:', error);
          setLoading(false);
          return;
        }

        if (session?.user) {
          console.log('✅ Session found for user:', session.user.email);
          const profile = await loadUserProfile(session.user.id);

          if (!mounted) return;

          if (!profile) {
            console.warn('⚠️ Profile not found, clearing session');
            await supabase.auth.signOut();
            setCurrentUser(null);
            setLoading(false);
            return;
          }

          setCurrentUser(profile);

          // Restore impersonation state if exists
          const storedImpersonation = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
          if (storedImpersonation && profile?.role === 'admin') {
            try {
              const impersonatedUser = JSON.parse(storedImpersonation);
              setViewingAsUser(impersonatedUser);
            } catch (e) {
              console.warn('⚠️ Invalid impersonation data, clearing');
              localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
            }
          }
        } else {
          console.log('ℹ️ No active session found');
        }

        if (mounted) {
          setLoading(false);
          console.log('✅ Auth initialization complete');
        }
      } catch (error) {
        console.error('❌ Exception during auth initialization:', error);
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initAuth();

    return () => {
      mounted = false;
    };

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) {
        const profile = await loadUserProfile(session.user.id);
        setCurrentUser(profile);
      } else {
        setCurrentUser(null);
        setViewingAsUser(null);
        localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log('🔐 Attempting login for:', email);

      // Sign in with Supabase Auth
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('❌ Auth error:', error);
        return {
          success: false,
          error: error.message || 'Invalid email or password'
        };
      }

      if (!data.user) {
        console.error('❌ No user data returned from auth');
        return {
          success: false,
          error: 'Login failed'
        };
      }

      console.log('✅ Auth successful, user ID:', data.user.id);
      console.log('User email:', data.user.email);

      // Load user profile
      const profile = await loadUserProfile(data.user.id);

      if (!profile) {
        console.error('❌ Profile not found, signing out');
        await supabase.auth.signOut();
        return {
          success: false,
          error: 'User profile not found. Please contact your administrator.'
        };
      }

      if (!profile.is_active) {
        await supabase.auth.signOut();
        return {
          success: false,
          error: 'Your account has been deactivated. Please contact your administrator.'
        };
      }

      // Update last login
      await supabase
        .from('profiles')
        .update({ last_login: new Date().toISOString() })
        .eq('id', profile.id);

      profile.last_login = new Date().toISOString();
      setCurrentUser(profile);

      return { success: true };
    } catch (error: any) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.message || 'An unexpected error occurred'
      };
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setCurrentUser(null);
    setViewingAsUser(null);
    localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
  };

  const startImpersonation = async (userId: string) => {
    if (!currentUser || currentUser.role !== 'admin') {
      console.error('Only admins can impersonate users');
      return;
    }

    try {
      const { data: userToImpersonate, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error || !userToImpersonate) {
        console.error('User not found');
        return;
      }

      if (userToImpersonate.id === currentUser.id) {
        console.error('Cannot impersonate yourself');
        return;
      }

      setViewingAsUser(userToImpersonate as Profile);
      localStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(userToImpersonate));
    } catch (error) {
      console.error('Error starting impersonation:', error);
    }
  };

  const stopImpersonation = () => {
    setViewingAsUser(null);
    localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
  };

  const isAdmin = (): boolean => {
    return currentUser?.role === 'admin';
  };

  // Achtung: 'caller' erbt die komplette Kunden-Ansicht (eingeschränkte Tabs,
  // kein Lead-Anlegen/Löschen/Bearbeiten). Die einzige Erweiterung ist der
  // Cal.com-Button im Lead-Detail — der wird über isCaller() gesteuert.
  const isKunde = (): boolean => {
    const role = (viewingAsUser || currentUser)?.role;
    return role === 'kunde' || role === 'caller';
  };

  const isCaller = (): boolean => {
    return (viewingAsUser || currentUser)?.role === 'caller';
  };

  const isImpersonating = (): boolean => {
    return viewingAsUser !== null;
  };

  const getEffectiveUser = (): Profile | null => {
    return viewingAsUser || currentUser;
  };

  const updateCurrentUser = async (updates: Partial<Profile>) => {
    if (!currentUser) return;

    try {
      // Update in database
      const { error } = await supabase
        .from('profiles')
        .update(updates as any)
        .eq('id', currentUser.id);

      if (error) {
        console.error('Error updating profile:', error);
        return;
      }

      // Update local state
      const updatedUser = { ...currentUser, ...updates };
      setCurrentUser(updatedUser);
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  const value: AuthContextType = {
    currentUser,
    viewingAsUser,
    isAuthenticated: currentUser !== null && !loading,
    isAdmin,
    isKunde,
    isCaller,
    login,
    logout,
    startImpersonation,
    stopImpersonation,
    isImpersonating,
    getEffectiveUser,
    updateCurrentUser
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
